// ./src/components/features/Dashboard/DashboardVehicleGrid/DashboardVehicleGrid.tsx
import React from "react";
import {
  SimpleGrid,
  Stack,
  Text,
  Paper,
  Skeleton,
  Button,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { Car } from "@/utils/api/CarService";
import { ReceiptScanResult } from "@/utils/api/ScannerService";

import { DetectLocationFn } from "@/hooks/useScanner";

import { useDashboardContext } from "@/contexts/DashboardContext";

import { AppIcon } from "@/components/generic/AppIcon";

import { VehicleCard, QuickActionType } from "@/components/features/Vehicle";

// ============================================
// Types
// ============================================

export interface DashboardVehicleGridProps {
  /** Handler for quick action click */
  onQuickAction?: (action: QuickActionType, carId: string) => void;

  /** Handler for vehicle card header click */
  onVehicleClick?: (car: Car) => void;

  /** Handler for "Add Vehicle" button */
  onAddVehicle?: () => void;

  /** Maximum number of vehicles to show (0 = show all) */
  maxVehicles?: number;

  /** Whether to show scan receipt on vehicle cards */
  showScanReceipt?: boolean;

  /** Handler for scan receipt completion */
  onScanComplete?: (result: ReceiptScanResult, carId?: string | null) => void;

  /** Optional location detection function for scan pipeline */
  onDetectLocation?: DetectLocationFn;

  /** Custom class name */
  className?: string;
}

// ============================================
// Sub-components
// ============================================

const VehicleGridSkeleton: React.FC<{ count: number }> = ({ count }) => (
  <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
    {Array.from({ length: count }).map((_, index) => (
      <Skeleton key={index} height={280} radius="md" />
    ))}
  </SimpleGrid>
);

interface VehicleGridEmptyProps {
  onAddVehicle?: () => void;
}

const VehicleGridEmpty: React.FC<VehicleGridEmptyProps> = ({
  onAddVehicle,
}) => {
  const { t } = useTranslation();

  return (
    <Paper radius="md" p="xl" withBorder>
      <Stack align="center" gap="md" py="xl">
        <AppIcon iconName="Car" size={64} color="var(--mantine-color-gray-4)" />
        <Stack align="center" gap="xs">
          <Text size="lg" fw={500}>
            {t("dashboard.noVehiclesYet")}
          </Text>
          <Text size="sm" c="dimmed" ta="center" maw={300}>
            {t("dashboard.noVehiclesDescription")}
          </Text>
        </Stack>
        {onAddVehicle && (
          <Button
            variant="filled"
            leftSection={<AppIcon iconName="Plus" size={18} />}
            onClick={onAddVehicle}
          >
            {t("actions.addVehicle")}
          </Button>
        )}
      </Stack>
    </Paper>
  );
};

// ============================================
// Component
// ============================================

/**
 * Displays a responsive grid of vehicle cards.
 * Shows loading skeletons while data is loading, and empty state if no vehicles.
 *
 * Consumes DashboardContext for cars and stats (single API call).
 * Passes DashboardItem to each VehicleCard — all aggregation and
 * unit conversions are done server-side.
 */
export const DashboardVehicleGrid: React.FC<DashboardVehicleGridProps> = ({
  onQuickAction,
  //onVehicleClick,
  onAddVehicle,
  maxVehicles = 0,
  showScanReceipt = false,
  onScanComplete,
  onDetectLocation,
  className,
}) => {
  const { items, cars, loading } = useDashboardContext();

  // Determine which items to display
  const displayItems =
    maxVehicles > 0 ? items.slice(0, maxVehicles) : items;

  // Loading state
  if (loading && cars.length === 0) {
    return <VehicleGridSkeleton count={3} />;
  }

  // Empty state
  if (!loading && cars.length === 0) {
    return <VehicleGridEmpty onAddVehicle={onAddVehicle} />;
  }

  return (
    <SimpleGrid
      cols={{ base: 1, sm: 2, lg: 2 }}
      spacing="md"
      className={className}
    >
      {displayItems.map((dashboardItem) => {
        const car = dashboardItem.car;
        if (!car) return null;

        return (
          <VehicleCard
            key={dashboardItem.carId}
            car={car}
            item={dashboardItem}
            bodyTypeId={car.bodyTypeId}
            showQuickActions
            onQuickAction={onQuickAction}
            showScanReceipt={showScanReceipt}
            onScanComplete={onScanComplete}
            onDetectLocation={onDetectLocation}
          //onHeaderClick={onVehicleClick}
          />
        );
      })}
    </SimpleGrid>
  );
};